import type { CardInput, PackDetail, PackInput, PackSummary, SuggestionInput } from '@shared/types';
import { getAdminToken, saveAdminToken } from './storage';

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit & { json?: unknown; admin?: boolean } = {}): Promise<T> {
  const headers: Record<string, string> = { ...(init.headers as Record<string, string>) };
  if (init.json !== undefined) headers['content-type'] = 'application/json';
  if (init.admin) headers.authorization = `Bearer ${getAdminToken()}`;
  let res: Response;
  try {
    res = await fetch(`/api${path}`, { ...init, headers, body: init.json !== undefined ? JSON.stringify(init.json) : init.body });
  } catch {
    throw new ApiError(0, 'Could not reach the server. Check your connection.');
  }
  const body = res.headers.get('content-type')?.includes('application/json') ? await res.json() : await res.text();
  if (!res.ok) {
    if (res.status === 401 && init.admin) saveAdminToken(null);
    throw new ApiError(res.status, (body as { error?: string })?.error ?? `Request failed (${res.status}).`);
  }
  return body as T;
}

export const api = {
  packs: () => request<PackSummary[]>('/packs'),
  /** A pack with a sample of its cards, or all of them for its maker (edit key or signed-in owner). */
  pack: (code: string, editKey = '') =>
    request<PackDetail>(`/packs/${encodeURIComponent(code)}`, editKey ? { headers: { 'x-edit-key': editKey } } : {}),
  canEditPack: (code: string, key: string) =>
    request<{ ok: boolean }>(`/packs/${encodeURIComponent(code)}/can-edit`, { headers: { 'x-edit-key': key } }),
  createPack: (input: PackInput) => request<{ code: string; editKey: string }>('/packs', { method: 'POST', json: input }),
  updatePack: (code: string, key: string, input: PackInput) =>
    request<{ ok: true }>(`/packs/${encodeURIComponent(code)}`, { method: 'PUT', json: input, headers: { 'x-edit-key': key } }),
  deletePack: (code: string, key: string) =>
    request<{ ok: true }>(`/packs/${encodeURIComponent(code)}`, { method: 'DELETE', headers: { 'x-edit-key': key } }),
  suggest: (input: SuggestionInput) => request<{ ok: true }>('/suggestions', { method: 'POST', json: input }),
};

export interface AdminPack extends PackSummary {
  active: boolean;
  sortOrder: number;
  createdAt: string;
}

export interface AdminCard extends CardInput {
  id: string;
  packId: string;
  active: boolean;
  plays: number;
  wins: number;
}

export interface Suggestion {
  id: string;
  type: 'prompt' | 'answer';
  text: string;
  pick: number;
  gloss: string;
  explanation: string;
  packId: string | null;
  packName: string | null;
  submitterName: string;
  source: 'form' | 'game' | 'write_in';
  roomCode: string | null;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: string;
}

export interface AdminSummary {
  totals: { games: number; games7: number; games30: number; players30: number; finished: number };
  daily: { day: string; games: number }[];
  topCards: { id: number; text: string; plays: number; wins: number; pack_name: string }[];
  topPrompts: { id: number; text: string; plays: number; pack_name: string }[];
  pendingSuggestions: number;
  live: {
    roomCount: number;
    players: number;
    connected: number;
    rooms: { code: string; phase: string; players: number; online: number; round: number; updatedAt: number }[];
  };
}

export const admin = {
  login: async (password: string) => {
    const { token } = await request<{ token: string }>('/admin/login', { method: 'POST', json: { password } });
    saveAdminToken(token);
  },
  summary: () => request<AdminSummary>('/admin/summary', { admin: true }),
  suggestions: (status: string) => request<Suggestion[]>(`/admin/suggestions?status=${status}`, { admin: true }),
  approve: (id: string, card: CardInput & { packId: string }) =>
    request(`/admin/suggestions/${id}/approve`, { method: 'POST', json: card, admin: true }),
  reject: (id: string) => request(`/admin/suggestions/${id}/reject`, { method: 'POST', admin: true }),
  packs: () => request<AdminPack[]>('/admin/packs', { admin: true }),
  createPack: (input: { name: string; description?: string; slug?: string; nsfw?: boolean; isDefault?: boolean }) =>
    request<{ id: string }>('/admin/packs', { method: 'POST', json: input, admin: true }),
  updatePack: (id: string, patch: Partial<AdminPack>) => request(`/admin/packs/${id}`, { method: 'PATCH', json: patch, admin: true }),
  deletePack: (id: string) => request(`/admin/packs/${id}`, { method: 'DELETE', admin: true }),
  cards: (packId: string, q = '', type = '') =>
    request<AdminCard[]>(`/admin/packs/${packId}/cards?q=${encodeURIComponent(q)}&type=${type}`, { admin: true }),
  addCard: (packId: string, card: CardInput) => request<AdminCard>(`/admin/packs/${packId}/cards`, { method: 'POST', json: card, admin: true }),
  updateCard: (id: string, patch: Partial<AdminCard>) => request(`/admin/cards/${id}`, { method: 'PATCH', json: patch, admin: true }),
  deleteCard: (id: string) => request(`/admin/cards/${id}`, { method: 'DELETE', admin: true }),
  importCsv: (packId: string, csv: string, mode: 'append' | 'replace') =>
    request<{ inserted: number; skipped: number }>(`/admin/packs/${packId}/import`, { method: 'POST', json: { csv, mode }, admin: true }),
  exportCsv: async (packId: string) => {
    const res = await fetch(`/api/admin/packs/${packId}/export.csv`, { headers: { authorization: `Bearer ${getAdminToken()}` } });
    if (!res.ok) throw new ApiError(res.status, 'Export failed.');
    return res.text();
  },
};
